import { CALENDLY_PREP_CUSTOM_ANSWER_KEY } from './meetingAgendaConstants';

/** BBA monthly check-in call scheduling (inline/popup widget URL). */
export const BBA_CHECK_IN_CALENDLY_URL =
  'https://calendly.com/ianr-lhxe/bba-check-in?hide_event_type_details=1&hide_gdpr_banner=1&primary_color=ff0000';

/** Kickoff call new clients book from Getting Started before their start date. */
export const BBA_KICKOFF_CALENDLY_URL =
  'https://calendly.com/d/ctm8-np8-d93/bba-kickoff-call?hide_gdpr_banner=1&primary_color=ff0000';

export { CALENDLY_PREP_CUSTOM_ANSWER_KEY };

/**
 * Builds name/email prefill from Firebase auth user + Firestore profile.
 * @param {import('firebase/auth').User | null | undefined} currentUser
 * @param {Record<string, unknown> | null | undefined} userProfile
 */
export function buildUserCalendlyPrefill(currentUser, userProfile) {
  if (!currentUser && !userProfile) return {};

  const firstName =
    userProfile?.firstName?.trim?.() ||
    currentUser?.displayName?.split(/\s+/)[0] ||
    currentUser?.email?.split('@')[0] ||
    '';
  const lastName =
    userProfile?.lastName?.trim?.() ||
    currentUser?.displayName?.split(/\s+/)?.slice(1)?.join(' ') ||
    '';
  const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();

  return {
    firstName,
    lastName,
    name: fullName || currentUser?.displayName || currentUser?.email?.split('@')[0] || '',
    email: currentUser?.email || '',
    phone: userProfile?.phone?.trim?.() || '',
  };
}

function hasCalendlyPrefillFields(prefill) {
  return (
    prefill &&
    (prefill.name ||
      prefill.email ||
      prefill.firstName ||
      prefill.lastName ||
      prefill.phone ||
      prefill.customAnswers)
  );
}

/**
 * Merges agenda text into Calendly prefill customAnswers.
 * @param {Record<string, string>} [basePrefill]
 * @param {string} [agendaText]
 */
export function mergeCalendlyPrefillWithAgenda(basePrefill, agendaText) {
  const trimmed = String(agendaText || '').trim();
  if (!trimmed) return basePrefill;

  return {
    ...basePrefill,
    customAnswers: {
      ...(basePrefill?.customAnswers || {}),
      [CALENDLY_PREP_CUSTOM_ANSWER_KEY]: trimmed,
    },
  };
}

function appendPrefillToUrl(url, prefill) {
  const params = new URLSearchParams();
  if (prefill?.name) params.set('name', prefill.name);
  if (prefill?.email) params.set('email', prefill.email);
  if (prefill?.firstName) params.set('first_name', prefill.firstName);
  if (prefill?.lastName) params.set('last_name', prefill.lastName);
  const agendaAnswer = prefill?.customAnswers?.[CALENDLY_PREP_CUSTOM_ANSWER_KEY];
  if (agendaAnswer) params.set(CALENDLY_PREP_CUSTOM_ANSWER_KEY, agendaAnswer);
  const qs = params.toString();
  if (!qs) return url;
  return `${url}${url.includes('?') ? '&' : '?'}${qs}`;
}

/**
 * Loads Calendly popup widget CSS/JS once (same pattern as PortalOperations).
 */
export function ensureCalendlyAssetsLoaded() {
  if (typeof document === 'undefined') return;
  if (!document.getElementById('calendly-widget-css')) {
    const link = document.createElement('link');
    link.id = 'calendly-widget-css';
    link.href = 'https://assets.calendly.com/assets/external/widget.css';
    link.rel = 'stylesheet';
    document.head.appendChild(link);
  }
  if (!document.getElementById('calendly-widget-js')) {
    const script = document.createElement('script');
    script.id = 'calendly-widget-js';
    script.src = 'https://assets.calendly.com/assets/external/widget.js';
    script.async = true;
    document.head.appendChild(script);
  }
}

/**
 * Opens Calendly in the official popup widget; falls back to a new tab if the script is not ready.
 * @param {string} url - Calendly scheduling URL (may include query params)
 * @param {Record<string, string>|undefined} prefill - optional name, email, firstName, lastName, phone, customAnswers
 * @param {{ agendaText?: string }} [options]
 */
export function openCalendlyPopup(url, prefill, options = {}) {
  const mergedPrefill = mergeCalendlyPrefillWithAgenda(prefill, options.agendaText);
  ensureCalendlyAssetsLoaded();

  const tryOpen = () => {
    if (typeof window !== 'undefined' && window.Calendly) {
      window.Calendly.initPopupWidget({
        url,
        prefill: hasCalendlyPrefillFields(mergedPrefill) ? mergedPrefill : undefined,
      });
      return true;
    }
    return false;
  };

  if (tryOpen()) return;

  let tries = 0;
  const id = window.setInterval(() => {
    tries += 1;
    if (tryOpen() || tries > 20) {
      window.clearInterval(id);
      if (tries > 20 && typeof window !== 'undefined') {
        window.open(appendPrefillToUrl(url, mergedPrefill), '_blank');
      }
    }
  }, 100);
}
